'use client';

import { useEffect, useMemo } from 'react';

import { setHipaaCustomerIds } from '@/analytics/hipaaCustomerId';
import { selectAccount } from '@/lib/account/accountSlice';
import { useAppSelector } from '@/lib/hooks';
import { selectRedeem } from '@/lib/redeem/redeemSlice';

/**
 * Bridges the session's enterprise customerIds from Redux into the module-level
 * holder that `hipaaMiddleware` reads. The middleware is a plain Segment
 * source-middleware function and can't read the store itself, so this component
 * (rendered inside the store tree) pushes the values in on every change.
 */
export function HipaaCustomerIdSync(): null {
  const account = useAppSelector(selectAccount);
  const redeem = useAppSelector(selectRedeem);

  const customerIds = useMemo(() => {
    const ids = [
      redeem.customerId,
      ...(account.orders?.map((o) => o.customerId) ?? []),
      ...(account.eligibilities?.map((e) => e.customer?.id) ?? []),
    ].filter((id): id is string => !!id);
    return Array.from(new Set(ids));
  }, [account.orders, account.eligibilities, redeem.customerId]);

  useEffect(() => {
    setHipaaCustomerIds(customerIds);
  }, [customerIds]);

  useEffect(() => {
    return () => setHipaaCustomerIds([]);
  }, []);

  return null;
}

export default HipaaCustomerIdSync;
